<script>
  import Session from './Session.svelte'
  import { decodeWord } from './wordEngine.js'
  import { buildCustomWord } from './content.js'
  import { loadAudioManifest, unlockAudio } from './audio.js'
  import { LESSONS } from './curriculum/ufli.js'
  import { CHILDREN, loadProgress, saveProgress, addPracticeMore } from './curriculum/progress.js'

  // Tutor-led lessons. Pick a lesson → a CHOOSE screen listing every part (new
  // sound(s), sample words, word chains, irregular words). Select the ones to
  // practice (unselect the sound to skip it), then ▶ Practice runs just those in
  // the shared monkey-and-parrot Session format.
  let { onexit } = $props()

  let lesson = $state(null)
  let phase = $state('grid')   // grid | select | session
  let items = $state([])       // [{ key, group, label, sub?, step }]
  let sel = $state(new Set())
  let sessionSteps = $state([])
  let sessionKey = $state(0)
  let loading = $state(false)
  let openSeq = 0

  const enrich = (w) => buildCustomWord(w, decodeWord(w))
  const groups = $derived([...new Set(items.map((i) => i.group))])
  const selectedCount = $derived(sel.size)

  async function open(ls) {
    unlockAudio()
    const my = ++openSeq
    loading = true
    const built = []
    const manifest = {}
    const seenWord = new Set()
    if (ls.intro && ls.intro.length) {
      for (const it of ls.intro) {
        if (it.code) Object.assign(manifest, buildCustomWord(it.display, [{ text: it.display, code: it.code }]).manifest)
        built.push({ key: 's:' + it.display, group: 'New sound', label: it.display, sub: it.sound, step: { type: 'intro', item: it } })
      }
    } else {
      built.push({ key: 'concept', group: 'New sound', label: ls.concept, step: { type: 'intro', item: { display: ls.concept } } })
    }
    const addWord = (w, group, type) => {
      if (seenWord.has(w)) return
      seenWord.add(w)
      const e = enrich(w); Object.assign(manifest, e.manifest)
      built.push({ key: group[0] + ':' + w, group, label: w, step: { type, text: w, word: e.word } })
    }
    for (const w of ls.words) addWord(w, 'Words', 'word')
    for (const w of ls.chains.flat()) addWord(w, 'Word chains', 'word')
    for (const w of ls.irregular) addWord(w, 'Irregular', 'tricky')
    await loadAudioManifest(manifest)
    if (my !== openSeq) return // superseded by a newer tap
    items = built
    sel = new Set(built.map((i) => i.key)) // default: everything selected
    lesson = ls
    phase = 'select'
    loading = false
  }

  function toggle(key) { const n = new Set(sel); if (n.has(key)) n.delete(key); else n.add(key); sel = n }
  function selectAll() { sel = new Set(items.map((i) => i.key)) }
  function selectNone() { sel = new Set() }
  function practice() {
    sessionSteps = items.filter((i) => sel.has(i.key)).map((i) => i.step)
    sessionKey += 1
    phase = 'session'
  }
  function toGrid() { lesson = null; items = []; sel = new Set(); phase = 'grid' }
  function toSelect() { phase = 'select' }
  function finishNote() { return '👍 Nice work!' }
  function onPracticeMore() {
    if (!lesson) return
    const words = [...new Set([...lesson.words, ...lesson.chains.flat(), ...lesson.irregular])]
    for (const c of CHILDREN) { const p = loadProgress(c.id); addPracticeMore(p, words); saveProgress(p) }
  }
</script>

{#if phase === 'session'}
  {#key sessionKey}
    <Session title={`Lesson ${lesson.id} · ${lesson.concept}`} steps={sessionSteps} backLabel="‹ Choose" onexit={toSelect} onfinish={finishNote} onpracticemore={onPracticeMore} source="tutoring" />
  {/key}
{:else if phase === 'select'}
  <div class="tutoring">
    <div class="toolbar"><button class="tb" onclick={toGrid} aria-label="Back to lesson list">‹ Lessons</button></div>
    <div class="head"><h2>Lesson {lesson.id}</h2><p>{lesson.concept}</p><p class="hint">Tap to choose what to practice</p></div>
    <div class="controls"><button class="mini" onclick={selectAll}>Select all</button><button class="mini" onclick={selectNone}>Clear</button></div>
    {#each groups as g}
      <section>
        <h3>{g}</h3>
        <div class="chips">
          {#each items.filter((i) => i.group === g) as i}
            <button class="chip" class:on={sel.has(i.key)} onclick={() => toggle(i.key)}>{i.label}{#if i.sub}<span class="csub"> {i.sub}</span>{/if}</button>
          {/each}
        </div>
      </section>
    {/each}
    <button class="practice" onclick={practice} disabled={selectedCount === 0}>▶ Practice ({selectedCount})</button>
  </div>
{:else}
  <div class="tutoring">
    <div class="toolbar"><button class="tb" onclick={onexit} aria-label="Back to menu">‹ Menu</button></div>
    <div class="head"><h2>🎓 Tutoring lessons</h2><p>Pick a lesson to practice</p></div>
    <div class="grid">
      {#each LESSONS as ls}
        <button class="cell" onclick={() => open(ls)}><span class="no">{ls.id}</span><span class="concept">{ls.concept}</span></button>
      {/each}
    </div>
    {#if loading}<p class="loading">Loading…</p>{/if}
  </div>
{/if}

<style>
  /* Own scroll container — the app body is overflow:hidden (kiosk). */
  .tutoring { position: fixed; inset: 0; overflow-y: auto; display: flex; flex-direction: column; align-items: center; gap: 1.4vh; padding: 7vh 2vh 12vh; box-sizing: border-box; }
  .toolbar { position: fixed; top: 1.2vh; left: 1.4vh; z-index: 25; display: flex; gap: 1vh; }
  .tb { border: none; background: rgba(255,255,255,0.9); color: #2e5d1f; font-family: inherit; font-weight: 800; font-size: 1.7vh; padding: 0.8vh 1.5vh; border-radius: 999px; box-shadow: 0 3px 0 rgba(0,0,0,0.15); cursor: pointer; }
  .tb:active { transform: translateY(2px); box-shadow: 0 1px 0 rgba(0,0,0,0.15); }
  .head { text-align: center; color: #fffef0; }
  .head h2 { margin: 0; font-size: 3vh; text-shadow: 0 2px 0 rgba(0,0,0,0.15); }
  .head p { margin: 0.3vh 0 0; font-weight: 700; opacity: 0.95; }
  .head .hint { font-size: 1.7vh; opacity: 0.85; }

  .grid { width: min(1100px, 96vw); display: grid; grid-template-columns: repeat(auto-fill, minmax(16vh, 1fr)); gap: 1.1vh; }
  .cell { border: none; background: #fffef0; border-radius: 14px; padding: 1.2vh 1vh; cursor: pointer; display: flex; flex-direction: column; align-items: center; gap: 0.4vh; box-shadow: 0 5px 0 rgba(0,0,0,0.14); text-align: center; }
  .cell:active { transform: translateY(2px); box-shadow: 0 3px 0 rgba(0,0,0,0.14); }
  .no { min-width: 4.6vh; height: 3.2vh; padding: 0 0.8vh; border-radius: 999px; background: #10466e; color: #fff; display: flex; align-items: center; justify-content: center; font-weight: 900; font-size: 1.7vh; }
  .concept { color: #2e5d1f; font-weight: 800; font-size: 1.5vh; line-height: 1.1; }

  .controls { display: flex; gap: 1vh; }
  .mini { border: none; background: rgba(255,255,255,0.85); color: #2e5d1f; font-family: inherit; font-weight: 800; font-size: 1.6vh; padding: 0.7vh 1.6vh; border-radius: 999px; cursor: pointer; }
  section { width: min(900px, 96vw); background: rgba(255,255,255,0.94); border-radius: 16px; padding: 1.4vh 1.8vh; }
  section h3 { margin: 0 0 1vh; color: #10466e; font-size: 2vh; }
  .chips { display: flex; flex-wrap: wrap; gap: 1vh; }
  .chip { border: 2px solid #cfe3bf; background: #fff; color: #2e5d1f; font-family: inherit; font-weight: 800; font-size: 2.1vh; padding: 0.8vh 1.6vh; border-radius: 12px; cursor: pointer; opacity: 0.55; }
  .chip.on { background: #2e5d1f; color: #fffef0; border-color: #2e5d1f; opacity: 1; }
  .csub { font-size: 1.5vh; font-weight: 700; opacity: 0.85; }
  .practice { position: fixed; bottom: 2vh; left: 50%; transform: translateX(-50%); z-index: 26; border: none; background: #ff7043; color: #fff; font-family: inherit; font-weight: 800; font-size: 2.4vh; padding: 1.4vh 3.4vh; border-radius: 999px; box-shadow: 0 6px 0 rgba(0,0,0,0.2); cursor: pointer; }
  .practice:disabled { opacity: 0.45; cursor: default; box-shadow: 0 3px 0 rgba(0,0,0,0.15); }
  .practice:active:not(:disabled) { transform: translateX(-50%) translateY(2px); box-shadow: 0 4px 0 rgba(0,0,0,0.2); }
  .loading { position: fixed; bottom: 2vh; color: #fffef0; font-weight: 700; }
</style>
